import {
  AccountBalanceWalletOutlined,
  Functions as SigmaIcon,
  HelpOutline,
  AccountCircleOutlined,
  BadgeOutlined,
  BusinessCenterOutlined,
  CloudUploadOutlined,
  CommentOutlined,
  CreditScoreOutlined,
  DeleteOutlineOutlined,
  DescriptionOutlined,
  EditLocationAltOutlined,
  EditOutlined,
  EmailOutlined,
  Filter1Outlined,
  Flag,
  FlagOutlined,
  HistoryEduOutlined,
  HouseOutlined,
  HowToRegOutlined,
  ImageOutlined,
  LinkOffOutlined,
  LinkOutlined,
  LocationOnOutlined,
  NotesOutlined,
  OpenInNew,
  PersonOffOutlined,
  PersonOutlineOutlined,
  PhoneDisabledOutlined,
  PhoneEnabledOutlined,
  RepeatOutlined,
  SyncOutlined,
  UnsubscribeOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { blue, green, grey, orange, purple, red } from "@mui/material/colors";
import { isNil, size } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { emailInRoles } from "../../../constants/selectOptions/selectOptionsLoanRoles";
import FlxGrid from "../../../_src_shared/components/layout/FlxGrid";
import { formatPhone } from "../../../_src_shared/utils/functions/formatPhone";
import { detailSidebarClear } from "../../../redux/reducers/detailSidebarReducer";
import SidebarTabSwitcher from "../../borrowerDetails/shared/SidebarTabSwitcher";
import JsonPreview from "../../common/JsonPreview";
import LoanEnhancedCommentTextBox from "../../common/Loan/LoanEnhancedCommentTextBox";
import SearchInput from "../../inputs/SearchInput";
import Flx from "../../layout/Flx";
import { slate, teal } from "../../../theme/colors";
import TabContent from "../../navigation/TabContent";
import Txt from "../../typography/Txt";
import ConfirmationDialog from "../../ui/ConfirmationDialog";
import SidebarBodyContent from "../components/SidebarBodyContent";
import SidebarBodyContentDivider from "../components/SidebarBodyContentDivider";
import SidebarDetailSection from "../components/SidebarDetailSection";
import ResizeableSidebar from "../ResizeableSidebar";
import { PanelIconButton } from "../shared/panelChrome";
import ContentArea from "../shared/ContentArea";
import PanelCard, { PanelCardCollapseProvider } from "../shared/PanelCard";
import CardCollapseButtons from "../shared/CardCollapseButtons";
import SubTabSwitcher from "../shared/SubTabSwitcher";
import useCardMasonry, {
  SIDEBAR_DEFAULT_WIDTH,
} from "../shared/useCardMasonry";
import PanelRow from "../shared/PanelRow";
import { FIELD_TYPE, fieldPassesSearch } from "../shared/sidebarFieldUtils";
import SidebarSection from "../shared/SidebarSection";
import LoanBorrowerSidebarActions from "./LoanBorrowerSidebarActions";
import LoanBorrowerSidebarSummaryWidgets from "./LoanBorrowerSidebarSummaryWidgets";
import ApplicantKeyDatesSection from "../../../screens/LoanDrilldownScreen/verificationDocs/ApplicantKeyDatesSection";
import EventNoteOutlined from "@mui/icons-material/EventNoteOutlined";

// ── Form imports for edit mode ──
import { isDocHidden } from "../../../_src_shared/utils/functions/isDocHidden";
import RffLoanBorrowerExperienceTableChanges from "../../finalForm/features/RffLoanBorrowerExperienceTableChanges";
import BorrowerExperienceEditor from "./BorrowerExperienceEditor";
import RffAddressEditingField from "../../finalForm/inputs/RffAddressEditingField";
import RffBooleanCheckbox from "../../finalForm/inputs/RffBooleanCheckbox";
import RffDateField from "../../finalForm/inputs/RffDateField";
import RffDollarField from "../../finalForm/inputs/RffDollarField";
import RffEmailField from "../../finalForm/inputs/RffEmailField";
import RffFloatField from "../../finalForm/inputs/RffFloatField";
import { fmtPoints } from "../../../screens/LoanDrilldownScreen/experience/experienceConstants";
import RffNumberField from "../../finalForm/inputs/RffNumberField";
import RffPercentField from "../../finalForm/inputs/RffPercentField";
import RffPhoneField from "../../finalForm/inputs/RffPhoneField";
import RffSelectField from "../../finalForm/inputs/RffSelectField";
import RffSelectMultipleField from "../../finalForm/inputs/RffSelectMultipleField";
import RffSsnField from "../../finalForm/inputs/RffSsnField";
import RffTextareaField from "../../finalForm/inputs/RffTextareaField";
import RffTextField from "../../finalForm/inputs/RffTextField";
import RffFormChanges from "../../finalForm/RffFormChanges";
import RffConditional from "../../finalForm/shared/RffConditional";
import {
  EditSearchProvider,
  SearchableField,
  SearchableSection,
} from "../../finalForm/shared/SearchableEditField";
import BorrowerAddressEditor from "./BorrowerAddressEditor";

// ── Hook for borrower updates ──
import EnhancedCommentItemRenderer from "../../../_src_shared/comments/EnhancedCommentItemRenderer";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import SummaryWidget, {
  SUMMARY_WIDGET_THEMES,
} from "../../widgets/SummaryWidget";
import { selectOptionsEstimatedFico } from "../../../constants/selectOptions/selectOptionsEstimatedFico";
import { selectOptionsHousingEvents } from "../../../constants/selectOptions/selectOptionsHousingEvents";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { useCommentUtilsHook } from "../../../hooks/utils/useCommentUtilsHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";
import QuickActionButton from "../../borrowerDetails/components/QuickActionButton";
import LoanBorrowerAssociatedFilesTable from "../../../shared/tables/LoanBorrowerAssociatedFilesTable";
import RecordDocCoveragePanel from "../../../screens/LoanDrilldownScreen/shared/RecordDocCoveragePanel";
import useAssociationUtils from "../../associations/hooks/useAssociationUtils";
import AssignLoanFilesButton from "../../common/Loan/buttons/AssignLoanFilesButton";
import { KpiWidget } from "../../data/KpiWidgetGroup";
import RffRowGroup from "../../finalForm/shared/RffRowGroup";
import LoanBorrowerFileUploadProcess from "../../processes/LoanBorrowerFileUploadProcess";
import RffPendingChangesBanner from "../shared/RffPendingChangesBanner";
import SidebarSectionTitle from "../shared/SidebarSectionTitle";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";

/* ─── helpers ─── */
const fmt = (v) => (v != null ? `$${Number(v).toLocaleString()}` : "—");
const pct = (v) => (v != null ? `${(Number(v) * 100).toFixed(0)}%` : "—");
const fmtDate = (d) => formatDate(d, "—", "MMM d, yyyy");

const ficoColor = (score) => {
  if (!score || score === 0) return grey[400];
  if (score >= 720) return green[800];
  if (score >= 680) return orange[700];
  return red[600];
};

/* ── Select options ── */
const selectOptionsCitizenshipStatus = [
  "US Citizen",
  "Foreign National with SSN",
  "Foreign National",
  "Foreign National Non-Resident",
  "Other - Options do not apply",
];

const selectOptionsMaritalStatus = ["Married", "Not Married"];

const selectOptionsIdType = [
  "Driver License",
  "Passport",
  "State ID",
  "Military ID",
  "Other",
];

const selectOptionsTimeSinceHousingEvent = [
  "Within the last 12-months",
  "Within the last 2-years",
  "Greater than 2-years ago",
];

const mortgageLateSelections = [
  "1x30x12",
  "1x60x24",
  "1x90",
  "2x30x12",
  "2x60x24",
  "2x90",
  "3x30x12",
  "3x60x24",
  "3x90",
  "4+ 30-day lates",
  "4+ 60-day lates",
  "4+ 90-day lates",
];
const BODY_BACKGROUND_COLOR = "#f5f6fa";
// PanelCard + PanelRow wrapper — mirrors the FieldsSection helper in
// LoanSubjectPropertySidebar so the Applicant Info tab renders inline-editable
// rows with the same chrome (light-blue editable cell, "Edited" pill, lock /
// computed indicators) as the Subject Property Details tab.
const FieldsSection = ({
  title,
  fields = [],
  icon,
  badge,
  quickFilter,
  subTitle,
  defaultOpen = true,
  variant,
  disableCollapse,
  children,
  sx,
}) => {
  const visibleFields = useMemo(
    () => fields.filter((f) => fieldPassesSearch(f, quickFilter)),
    [fields, quickFilter],
  );
  if (visibleFields.length === 0 && !children) return null;

  const isFiltered = visibleFields.length < fields.length;
  const resolvedBadge =
    badge ??
    (isFiltered ? `${visibleFields.length}/${fields.length}` : undefined);

  return (
    <PanelCard
      title={title}
      icon={icon}
      badge={resolvedBadge}
      defaultOpen={defaultOpen}
      // Matches the Loan Info redesign tab's card chrome — these sit in the
      // same masonry layout and should read as the same object.
      variant={variant || "loanInfo"}
      subTitle={subTitle}
      disableCollapse={disableCollapse}
      sx={{ mb: 0, ...sx }}
    >
      {visibleFields.map((f, i) => (
        <PanelRow
          key={f.label}
          label={f.label}
          name={f.name}
          editor={f.editor}
          value={f.value}
          editable={f.fieldType === FIELD_TYPE.EDITABLE}
          computed={f.fieldType === FIELD_TYPE.COMPUTED}
          computedTooltip={f.computedTooltip}
          infoTooltip={f.infoTooltip}
          blockedReason={f.blockedReason}
          state={f.state}
          onAdd={f.onAdd}
          onValueClick={f.onValueClick}
          valueClickHint={f.valueClickHint}
          searchTerms={f.searchTerms}
          quickFilter={quickFilter}
          isLast={i === visibleFields.length - 1 && !children}
        />
      ))}
      {children}
    </PanelCard>
  );
};

/* ═══════════════════════════════════════════
   Edit Section IDs
   ═══════════════════════════════════════════ */
const EDIT_SECTIONS = {
  ALL: "all",
  CONTACT: "contact",
  CREDIT: "credit",
  ROLE: "role",
  LIQUIDITY: "liquidity",
  EXPERIENCE: "experience",
  BACKGROUND: "background",
  NOTES: "notes",
  ADDRESS: "address",
};

/* ═══════════════════════════════════════════
   TAB / FILTER CONSTANTS
   ═══════════════════════════════════════════ */
const TABS = {
  OVERVIEW: "Overview",
  APPLICANT_INFO: "Information",
  FILES: "Documents",
  KEY_DOCUMENTS: "Key Docs & Reports",
  COMMENTS: "Comments",
  JSON: "JSON",
};

// Which Documents-tab layout the sidebar renders:
//   "assigned" — the Applicants-tab card's Assigned Documents rail
//                (RecordDocCoveragePanel, group cards in wrapping columns)
//   "classic"  — Upload card + Related Borrower Files table
// Flip back to "classic" to revert; both stay in the codebase.
const FILES_TAB_LAYOUT = "assigned";

/**
 * Sub-tab labels mirror the card titles they filter to, so the switcher reads
 * as a table of contents for what's below it. Order matches the card order in
 * the masonry. Experience is the one tab heading a group rather than a single
 * card — it also carries the self-reported and validated cards.
 */
const BORROWER_TABS = {
  ALL: "All",
  DETAILS: "Contact & Identity",
  LIQUIDITY: "Liquidity",
  LOAN_ROLE: "Loan Role & Ownership",
  CREDIT: "Credit & Scores",
  BACKGROUND: "Housing Events & History",
  EXPERIENCE: "Experience",
  NOTES: "Notes",
  ADDRESS_DETAILS: "Address",
};

/**
 * How many cards each sub-tab renders into the Information masonry. Feeds the
 * column cap the same way the Loan Information tab keys `columns` to its card
 * count — a zone with two cards gets two half-width columns rather than three
 * columns with an empty one on the right.
 *
 * Experience is 3 because ExperienceSection emits a fragment (fields card +
 * self-reported table + validated properties); every other tab is one card.
 */
const BORROWER_TAB_CARD_COUNTS = {
  [BORROWER_TABS.ALL]: 9,
  [BORROWER_TABS.DETAILS]: 1,
  [BORROWER_TABS.LOAN_ROLE]: 1,
  [BORROWER_TABS.LIQUIDITY]: 1,
  [BORROWER_TABS.CREDIT]: 1,
  [BORROWER_TABS.BACKGROUND]: 1,
  [BORROWER_TABS.EXPERIENCE]: 3,
  [BORROWER_TABS.NOTES]: 1,
  [BORROWER_TABS.ADDRESS_DETAILS]: 1,
};
// "All" excludes the address card — see the Address block in the tab body.

const BorrowerCommentsRenderer = ({
  comments,
  borrowerComments,
  borrowerId,
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { getCommentsAssociatedWithBorrower } = useCommentUtilsHook();

  const associatedComments = useMemo(() => {
    const targetId = borrowerId;
    return getCommentsAssociatedWithBorrower(loanDrilldown?.comments, targetId);
  }, [getCommentsAssociatedWithBorrower, loanDrilldown?.comments, borrowerId]);
  // const { getCommentsAssociatedWithBorrower } = useCommentUtilsHook();

  // const associatedComments = useMemo(() => {
  //   if (isArray(comments)) {
  //     return comments;
  //   }
  //   const targetId = borrowerId;
  //   return getCommentsAssociatedWithBorrower(loanDrilldown?.comments, targetId);
  // }, [
  //   comments,
  //   getCommentsAssociatedWithBorrower,
  //   loanDrilldown?.comments,
  //   borrowerId,
  // ]);
  return (
    <>
      <EnhancedCommentItemRenderer
        loan={loanDrilldown}
        padding={0}
        comments={borrowerComments || associatedComments}
      />
    </>
  );
};

/* ═══════════════════════════════════════════
   Metric Bar — monospace highlighted stats
   ═══════════════════════════════════════════ */
const MetricBar = ({ borrower }) => {
  const totalLiq =
    (borrower?.cashLiquidity || 0) + (borrower?.otherLiquidity || 0);

  return (
    <Flx
      ac
      sx={{
        bgcolor: grey[50],
        border: `1px solid ${grey[200]}`,
        borderRadius: "8px",
        p: 1.5,
        gap: 0,
      }}
    >
      <MetricItem
        label="FICO"
        value={
          borrower?.ficoMid ? (
            <Flx center g={0.2}>
              <span>{borrower.ficoLow}</span>
              <Divider orientation="vertical" flexItem sx={{ m: 0.5 }} />
              <span>{borrower.ficoMid}</span>
              <Divider orientation="vertical" flexItem sx={{ m: 0.5 }} />
              <span>{borrower.ficoMax}</span>
            </Flx>
          ) : (
            "—"
          )
        }
        valueColor={ficoColor(borrower?.ficoMid)}
      />
      <MetricDivider />
      <MetricItem
        label="ENTITY OWN."
        value={pct(borrower?.entityPercentOwnership)}
      />
      <MetricDivider />
      <MetricItem label="LIQ" value={fmt(totalLiq)} />
      <MetricDivider />
      <MetricItem
        label="DOCUMENTS"
        value={size(borrower?.associations?.loanDocuments)}
        valueColor={
          size(borrower?.associations?.loanDocuments) > 0 ? null : grey[400]
        }
      />
    </Flx>
  );
};

const MetricItem = ({ label, value, valueColor }) => (
  <Flx column sx={{ flex: 1, textAlign: "center", minWidth: 0 }}>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: grey[500],
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        fontFamily: "'IBM Plex Mono', monospace",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontSize: 14.5,
        fontWeight: 700,
        color: valueColor || grey[800],
        mt: 0.3,
        fontFamily: "'IBM Plex Mono', monospace",
        letterSpacing: "-0.02em",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Txt>
  </Flx>
);

const MetricDivider = () => (
  <Box
    sx={{
      width: "1px",
      height: 28,
      bgcolor: grey[200],
      mx: 0.5,
      flexShrink: 0,
    }}
  />
);

/* ═══════════════════════════════════════════
   Badge row — classification + role chips
   ═══════════════════════════════════════════ */
export const LoanBorrowerBadgeRow = ({ borrower, sx }) => {
  return (
    <Flx ac g={0.5} sx={{ flexWrap: "wrap", ...sx }}>
      <BadgeChip
        value={borrower?.emailAddress}
        // validText={borrower?.emailAddress || "No email address"}
        validText={borrower?.emailAddress}
        invalidText={"No email address set"}
        validIcon={<EmailOutlined />}
        invalidIcon={<UnsubscribeOutlined />}
        color="green"
      />
      <BadgeChip
        validText={borrower?.phone ? formatPhone(borrower.phone) : null}
        value={borrower?.phone}
        validIcon={<PhoneEnabledOutlined />}
        invalidIcon={<PhoneDisabledOutlined />}
        invalidText="Missing number"
        color="green"
      />

      <BadgeChip
        value={borrower?.isGuarantor}
        validText="Guarantor"
        invalidText="Non-Guarantor"
        color={borrower?.isGuarantor ? "purple" : "grey"}
        validIcon={<HowToRegOutlined />}
        invalidIcon={<PersonOffOutlined />}
      />
      {/* Only chipped when linked — "Unlinked Portal User" is the default state
          for most borrowers, so it added a chip that said nothing. */}
      <BadgeChip
        value={borrower?.borrowerPortalUserId}
        validText="Linked Portal User"
        validIcon={<LinkOutlined />}
        invalidIcon={<LinkOffOutlined />}
        invalidText="Unlinked Portal User"
        color="green"
        hideIfInvalid
      />
      <BadgeChip
        value={borrower?.firstTimeHomebuyer}
        validText="First-Time Home Buyer"
        validIcon={<Filter1Outlined />}
        invalidText="Non-First-Time Home Buyer"
        color="orange"
        hideIfInvalid
      />
      <BadgeChip
        value={borrower?.repeatBorrowerFlag}
        validText="Repeat Borrower"
        invalidText="Non-Repeat Borrower"
        validIcon={<RepeatOutlined />}
        color="green"
        hideIfInvalid
      />
      <BadgeChip
        value={borrower?.isPrimaryContactForLoan}
        validText="Primary Contact"
        invalidText="Non-Primary Contact"
        color="blue"
        hideIfInvalid
      />
      <BadgeChip
        value={borrower?.open_liens_judgments}
        validText="Open Liens/Judgments"
        invalidText="No Open Liens/Judgments"
        validIcon={<Flag />}
        invalidIcon={<Flag />}
        color="red"
        hideIfInvalid
      />
      <BadgeChip
        value={borrower?.bankruptcy_flag}
        validText="Bankruptcy Flag"
        validIcon={<FlagOutlined />}
        color="red"
        hideIfInvalid
      />
      <BadgeChip
        value={borrower?.foreignNationalFlag}
        validText="Foreign National"
        invalidText="Non-Foreign National"
        validIcon={<FlagOutlined />}
        color="orange"
        hideIfInvalid
      />
    </Flx>
  );
};

const BadgeChip = ({
  value,
  validText,
  invalidText,
  color,
  hideIfInvalid = false,
  validIcon,
  invalidIcon,
}) => {
  const colorMap = {
    green: { bg: green[50], color: green[700] },
    red: { bg: red[50], color: red[700] },
    orange: { bg: orange[50], color: orange[800] },
    purple: { bg: purple[50], color: purple[800] },
    blue: { bg: blue[50], color: blue[700] },
    grey: { bg: grey[100], color: grey[600] },
  };

  const colorConfig = isNil(value)
    ? colorMap.grey
    : colorMap[color] || colorMap.grey;

  if (hideIfInvalid && !value) {
    return null;
  }
  return (
    <Chip
      label={value ? validText : invalidText}
      size="12mall"
      icon={value ? validIcon : invalidIcon}
      sx={{
        fontSize: 10.5,
        fontWeight: 600,
        height: 22,
        bgcolor: colorConfig.bg,
        color: colorConfig.color,
        pl: validIcon || invalidIcon ? 0.5 : 0,
        svg: {
          fontSize: "13px !important",
          color: `${colorConfig.color} !important`,
        },
      }}
    />
  );
};

/* ═══════════════════════════════════════════
   Guideline Alert Banner
   ═══════════════════════════════════════════ */
const GuidelineAlerts = ({ checks }) => {
  if (!checks?.length) return null;
  return (
    <Box
      sx={{
        mx: 2.5,
        mb: 1.5,
        p: 1.5,
        borderRadius: "8px",
        bgcolor: red[50],
        border: `1px solid ${red[200]}`,
      }}
    >
      <Txt
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          color: red[700],
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          mb: 0.5,
        }}
      >
        ⚠ Guideline Flags
      </Txt>
      {checks.map((g, i) => (
        <Txt key={i} sx={{ fontSize: 12.5, color: red[800], mt: i ? 0.3 : 0 }}>
          {g.reason}
        </Txt>
      ))}
    </Box>
  );
};

const DetailBox = ({ children, label, sx }) => {
  return (
    <Flx
      fw
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0.5,
        flexGrow: 1,
        flexDirection: "column",
        bgcolor: grey[50],
        p: 1.5,
        flexWrap: "wrap",
        justifyContent: "space-between",
        borderRadius: "8px",
        border: `1px solid ${grey[200]}`,
        ...sx,
      }}
    >
      {label && (
        <Txt
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: grey[500],
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          {label}
        </Txt>
      )}
      <Txt sx={{ fontSize: 13, color: grey[700], lineHeight: 1.6 }}>
        {children}
      </Txt>
    </Flx>
  );
};

/* ═══════════════════════════════════════════
   Validated Experience List
   ═══════════════════════════════════════════ */
const ValidatedExperienceList = ({ experience }) => {
  if (!experience?.length)
    return (
      <Flx column g={0.75} sx={{ py: 0.5 }}>
        <Txt sx={{ fontSize: 12.5, color: grey[500] }}>
          No validated experience records on this loan.
        </Txt>
        <Txt sx={{ fontSize: 11.5, color: grey[400], lineHeight: 1.5 }}>
          These aren't entered here — they come from the borrower's Portal
          account. Use <strong>Update Loan from Borrower's Portal Account</strong>{" "}
          on the Overview tab to pull them in (the borrower must be linked to a
          portal account first).
        </Txt>
      </Flx>
    );
  return (
    <Box
      sx={{
        borderRadius: "6px",
        border: `1px solid ${grey[200]}`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 150px 150px",
          bgcolor: grey[50],
          px: 1.5,
          py: 0.75,
        }}
      >
        {["Type", "Exp. Type", "Resolution Status"].map((h, i) => (
          <Txt
            key={h}
            sx={{
              fontSize: 10,
              fontWeight: 600,
              color: grey[500],
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              textAlign: i > 0 ? "right" : "left",
            }}
          >
            {h}
          </Txt>
        ))}
      </Box>

      {experience.map((v, i) => (
        <Box
          key={i}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 150px 150px",
            px: 1.5,
            py: 0.6,
            borderTop: `1px solid ${grey[100]}`,
          }}
        >
          <Txt sx={{ fontSize: 12, color: grey[600] }}>
            {addressLabel(v.propertyAddress) || "—"}
          </Txt>
          <Txt
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: grey[800],
              textAlign: "right",
            }}
          >
            {v.experienceType}
          </Txt>
          <Txt
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: grey[800],
              textAlign: "right",
            }}
          >
            {v.resolutionStatus ?? "—"}
          </Txt>
        </Box>
      ))}
    </Box>
  );
};

/* ═══════════════════════════════════════════
   EDIT MODE — Form Sections
   ═══════════════════════════════════════════ */
const EditContactFields = () => (
  <RffRowGroup>
    <SearchableField label="First Name" name="firstName">
      <RffTextField size={12} label="First Name" row name="firstName" />
    </SearchableField>
    <SearchableField label="Middle Name" name="middleName">
      <RffTextField size={12} label="Middle Name" row name="middleName" />
    </SearchableField>
    <SearchableField label="Last Name" name="lastName">
      <RffTextField size={12} label="Last Name" row name="lastName" />
    </SearchableField>
    <SearchableField label="Email Address" name="emailAddress">
      <RffEmailField size={12} label="Email Address" row name="emailAddress" />
    </SearchableField>
    <SearchableField label="Phone" name="phone" keywords="telephone number">
      <RffPhoneField size={12} label="Phone" row name="phone" />
    </SearchableField>
    <SearchableField label="Date of Birth" name="dateOfBirth" keywords="dob">
      <RffDateField size={12} label="Date of Birth" row name="dateOfBirth" />
    </SearchableField>
    <SearchableField label="Marital Status" name="maritalStatus">
      <RffSelectField
        size={12}
        label="Marital Status"
        row
        name="maritalStatus"
        options={selectOptionsMaritalStatus}
      />
    </SearchableField>
    <SearchableField
      label="Citizenship Status"
      name="citizenshipStatus"
      keywords="us citizen foreign national"
    >
      <RffSelectField
        label="Citizenship Status"
        row
        name="citizenshipStatus"
        options={selectOptionsCitizenshipStatus}
      />
    </SearchableField>
    <SearchableField
      label="ID Type"
      name="borrowerIdType"
      keywords="identification driver license passport"
    >
      <RffSelectField
        size={12}
        label="ID Type"
        row
        name="borrowerIdType"
        options={selectOptionsIdType}
      />
    </SearchableField>
    <SearchableField
      label="ID Expiration Date"
      name="idExpirationDate"
      keywords="identification"
    >
      <RffDateField
        size={12}
        label="ID Expiration Date"
        row
        name="idExpirationDate"
      />
    </SearchableField>
    <SearchableField
      label="Has a US issued SSN"
      name="borrowerHasSSN"
      keywords="ssn social security"
    >
      <RffBooleanCheckbox
        row
        name="borrowerHasSSN"
        label="Has a US issued SSN"
      />
    </SearchableField>
    <RffConditional field="borrowerHasSSN" value={true}>
      <SearchableField
        label="Social Security Number"
        name="borrowerSSN"
        keywords="ssn"
      >
        <RffSsnField row name="borrowerSSN" label="Social Security Number" />
      </SearchableField>
    </RffConditional>
    <SearchableField label="Company" name="company" keywords="entity business">
      <RffTextField size={12} label="Company" row name="company" />
    </SearchableField>
  </RffRowGroup>
);

const EditAddressFields = () => (
  <RffRowGroup>
    <RffConditional
      operatorForMultiple="AND"
      rules={[
        {
          field: "citizenshipStatus",
          operator: "!=",
          value: "Foreign National Non-Resident",
        },
        {
          field: "citizenshipStatus",
          operator: "!=",
          value: "Other - Options do not apply",
        },
      ]}
    >
      <SearchableField
        label="Borrower Primary Address"
        name="primaryAddress"
        keywords="street city state zip"
      >
        <RffAddressEditingField
          row
          label="Borrower Primary Address"
          name="primaryAddress"
        />
      </SearchableField>
    </RffConditional>
    <RffConditional
      operatorForMultiple="OR"
      rules={[
        {
          field: "citizenshipStatus",
          operator: "==",
          value: "Foreign National Non-Resident",
        },
        {
          field: "citizenshipStatus",
          operator: "==",
          value: "Other - Options do not apply",
        },
      ]}
    >
      <SearchableField
        label="Enter non-US address"
        name="nonUSborrowerAddress"
        keywords="foreign address"
      >
        <RffTextField
          row
          name="nonUSborrowerAddress"
          label="Enter non-US address"
        />
      </SearchableField>
    </RffConditional>
  </RffRowGroup>
);

const EditCreditFields = () => (
  <RffRowGroup>
    <SearchableField
      label="Estimated FICO Range"
      name="estimatedFICO"
      keywords="credit score"
    >
      <RffSelectField
        size={12}
        label="Estimated FICO Range"
        row
        name="estimatedFICO"
        options={selectOptionsEstimatedFico}
      />
    </SearchableField>
    <SearchableField
      label="Estimated Numeric FICO"
      name="estimatedNumericFICO"
      keywords="credit score"
    >
      <RffNumberField
        size={12}
        label="Estimated Numeric FICO"
        row
        name="estimatedNumericFICO"
      />
    </SearchableField>
    <SearchableField label="FICO Low" name="ficoLow" keywords="credit score">
      <RffNumberField size={12} label="FICO Low" row name="ficoLow" />
    </SearchableField>
    <SearchableField label="FICO Mid" name="ficoMid" keywords="credit score">
      <RffNumberField size={12} label="FICO Mid" row name="ficoMid" />
    </SearchableField>
    <SearchableField label="FICO High" name="ficoMax" keywords="credit score">
      <RffNumberField size={12} label="FICO High" row name="ficoMax" />
    </SearchableField>
    <SearchableField label="Credit Report Date" name="creditReportDate">
      <RffDateField
        size={12}
        label="Credit Report Date"
        row
        name="creditReportDate"
      />
    </SearchableField>
    <SearchableField
      label="Credit Auth Date"
      name="creditAuthDate"
      keywords="authorization"
    >
      <RffDateField
        size={12}
        label="Credit Auth Date"
        row
        name="creditAuthDate"
      />
    </SearchableField>
    <SearchableField label="Background Check Date" name="backgroundDate">
      <RffDateField
        size={12}
        label="Background Check Date"
        row
        name="backgroundDate"
      />
    </SearchableField>
    <SearchableField
      label="Mortgage Lates"
      name="mortgage_lates"
      keywords="late payments delinquency"
    >
      <RffSelectMultipleField
        label="Mortgage Lates"
        row
        name="mortgage_lates"
        options={mortgageLateSelections}
      />
    </SearchableField>
  </RffRowGroup>
);

const EditRoleFields = () => (
  <RffRowGroup>
    <SearchableField
      label="Entity Ownership %"
      name="entityPercentOwnership"
      keywords="ownership percent"
    >
      <RffPercentField
        size={12}
        label="Entity Ownership %"
        row
        name="entityPercentOwnership"
      />
    </SearchableField>
    <SearchableField
      label="Is a guarantor on the loan"
      name="isGuarantor"
      keywords="guarantor"
    >
      <RffBooleanCheckbox
        label="Is a guarantor on the loan"
        row
        name="isGuarantor"
      />
    </SearchableField>
    <SearchableField
      label="Is primary contact for the loan"
      name="isPrimaryContactForLoan"
      keywords="primary contact"
    >
      <RffBooleanCheckbox
        label="Is primary contact for the loan"
        row
        name="isPrimaryContactForLoan"
      />
    </SearchableField>
    <SearchableField
      label="Is a repeat borrower"
      name="repeatBorrowerFlag"
      keywords="repeat"
    >
      <RffBooleanCheckbox
        label="Is a repeat borrower"
        row
        name="repeatBorrowerFlag"
      />
    </SearchableField>
    <SearchableField
      label="Role"
      name="role"
      keywords="borrower broker correspondent"
    >
      <RffTextField size={12} label="Role" row name="role" />
    </SearchableField>
  </RffRowGroup>
);

const EditLiquidityFields = () => (
  <RffRowGroup>
    <SearchableField
      label="Total cash liquidity in personal and/or entity bank accounts"
      name="cashLiquidity"
      keywords="cash liquidity bank accounts"
    >
      <RffDollarField
        size={12}
        label="Total cash liquidity in personal and/or entity bank accounts"
        row
        name="cashLiquidity"
      />
    </SearchableField>
    <SearchableField
      label="Total value of other liquidity sources"
      name="otherLiquidity"
      keywords="other liquidity money market securities ira 401k"
    >
      <RffDollarField
        size={12}
        label="Total value of other liquidity sources"
        row
        name="otherLiquidity"
        helperText="Including money market/securities account, cash value of life insurance policies, IRA or 401k accounts"
      />
    </SearchableField>
    <SearchableField
      label="Verified Liquidity"
      name="verifiedLiquidity"
      keywords="verified underwriting"
    >
      <RffDollarField
        size={12}
        label="Verified Liquidity"
        row
        helperText={
          "The verified liquidity of the borrower based on bank statements or other documentation based on underwriting"
        }
        name="verifiedLiquidity"
      />
    </SearchableField>
  </RffRowGroup>
);

const EditExperienceFields = () => (
  <>
    <RffRowGroup>
      <SearchableField
        label="Experienced Borrower"
        name="isExperienced"
        keywords="is experienced"
      >
        <RffBooleanCheckbox
          row
          name="isExperienced"
          label="Borrower has prior real-estate investment experience"
        />
      </SearchableField>
      <SearchableField
        label="Fix & Flips"
        name="hasExperienceWithFixFlips"
        keywords="fix flip experience"
      >
        <RffBooleanCheckbox
          row
          name="hasExperienceWithFixFlips"
          label="Fix & Flips"
        />
      </SearchableField>
      <SearchableField
        label="Income Producing Properties"
        name="hasExperienceWithIncomeProducingProperties"
        keywords="rental experience"
      >
        <RffBooleanCheckbox
          row
          name="hasExperienceWithIncomeProducingProperties"
          label="Income Producing Properties"
        />
      </SearchableField>
      <SearchableField
        label="Ground Up Construction"
        name="hasExperienceWithGroundUpConstruction"
        keywords="construction experience"
      >
        <RffBooleanCheckbox
          row
          name="hasExperienceWithGroundUpConstruction"
          label="Ground Up Construction"
        />
      </SearchableField>
      <SearchableField
        label="Verified Experience Total"
        name="verifiedExperienceTotal"
        keywords="verified deals"
      >
        {/* POINTS, not a deal count — the line-item classifier scores an
            income-producing purchase and third-party GC work at 0.5, so this
            has to hold 2.5. */}
        <RffFloatField
          size={12}
          label="Verified Experience Total"
          row
          name="verifiedExperienceTotal"
        />
      </SearchableField>
      <SearchableField
        label="Summary of borrower property investor experience"
        name="borrowerExperienceSummary"
        keywords="experience summary notes"
      >
        <RffTextareaField
          minHeight={80}
          label="Summary of borrower property investor experience"
          row
          name="borrowerExperienceSummary"
        />
      </SearchableField>
    </RffRowGroup>
    <RffRowGroup>
      <RffLoanBorrowerExperienceTableChanges />
    </RffRowGroup>
  </>
);

const EditBackgroundFields = () => (
  <Flx column g={5} sx={{ py: 1 }}>
    <Flx column g={0}>
      <Txt
        bold
        sx={{
          fontSize: "12px",
          fontWeight: 500,
        }}
      >
        Criminal History
      </Txt>
      <RffRowGroup suppressBottomMargin>
        <SearchableField
          label="Been convicted of a felony?"
          name="application_felony_conviction"
          keywords="criminal felony"
        >
          <RffBooleanCheckbox
            row
            name="application_felony_conviction"
            label="Been convicted of a felony?"
          />
        </SearchableField>
        <SearchableField
          label="Been convicted of any financial or fraud related crimes including misdemeanors?"
          name="application_financial_fraud_misdemeanor"
          keywords="criminal fraud misdemeanor"
        >
          <RffBooleanCheckbox
            row
            name="application_financial_fraud_misdemeanor"
            label="Been convicted of any financial or fraud related crimes including misdemeanors?"
          />
        </SearchableField>
      </RffRowGroup>
    </Flx>

    <Flx column>
      <Txt bold sx={{ fontSize: "12px", fontWeight: 500 }}>
        Bankruptcy
      </Txt>
      <RffRowGroup suppressBottomMargin>
        <SearchableField
          label="Filed for bankruptcy in the last 5 years?"
          name="bankruptcy_flag"
          keywords="bankruptcy"
        >
          <RffBooleanCheckbox
            row
            name="bankruptcy_flag"
            label="Filed for bankruptcy in the last 5 years?"
          />
        </SearchableField>
        <RffConditional field="bankruptcy_flag" value={true}>
          <SearchableField
            label="Time Since Bankruptcy (years)"
            name="time_since_bankruptcy"
            keywords="bankruptcy"
          >
            <RffTextField
              size={12}
              label="Time Since Bankruptcy (years)"
              row
              name="time_since_bankruptcy"
            />
          </SearchableField>
        </RffConditional>
      </RffRowGroup>
    </Flx>

    <Flx column>
      <Txt bold sx={{ fontSize: "12px", fontWeight: 500 }}>
        Liens & Judgments
      </Txt>
      <RffRowGroup suppressBottomMargin>
        <SearchableField
          label="Charge offs, federal or state tax liens, pending litigation or financial judgments"
          name="open_liens_judgments"
          keywords="liens judgments charge offs tax pending litigation"
        >
          <RffBooleanCheckbox
            row
            name="open_liens_judgments"
            label="Charge offs, federal or state tax liens, pending litigation or financial judgments"
          />
        </SearchableField>
        <RffConditional field="open_liens_judgments" value={true}>
          <SearchableField label="Charge Offs" name="charge_offs">
            <RffBooleanCheckbox row name="charge_offs" label="Charge Offs" />
          </SearchableField>
          <SearchableField label="Tax Liens" name="tax_liens">
            <RffBooleanCheckbox row name="tax_liens" label="Tax Liens" />
          </SearchableField>
          <SearchableField
            label="Financial Judgements"
            name="financial_judgements"
          >
            <RffBooleanCheckbox
              row
              name="financial_judgements"
              label="Financial Judgements"
            />
          </SearchableField>
          <SearchableField label="Pending Litigation" name="pending_litigation">
            <RffBooleanCheckbox
              row
              name="pending_litigation"
              label="Pending Litigation"
            />
          </SearchableField>
        </RffConditional>
      </RffRowGroup>
    </Flx>

    <Flx column>
      <Txt bold sx={{ fontSize: "12px", fontWeight: 500 }}>
        Property & Housing History
      </Txt>
      <RffRowGroup suppressBottomMargin>
        <SearchableField
          label="Ever owned a primary residence?"
          name="firstTimeHomebuyer"
          keywords="first time homebuyer primary residence"
        >
          <RffBooleanCheckbox
            row
            name="firstTimeHomebuyer"
            label="Ever owned a primary residence?"
          />
        </SearchableField>
        <SearchableField
          label="Housing events"
          name="housing_events"
          keywords="forbearance modification lis pendens foreclosure mortgage lates deed in lieu short sale"
        >
          <RffSelectMultipleField
            name="housing_events"
            label="Entered into a loan forbearance or modification program, a property owned as individual or through an entity under your control subject to lis pendens or foreclosure or any mortgage lates?"
            options={selectOptionsHousingEvents}
            helperText="Select all that apply"
          />
        </SearchableField>
        <SearchableField
          label="Time Since Last Housing Event"
          name="time_since_housing_event"
        >
          <RffSelectField
            row
            name="time_since_housing_event"
            label="Time Since Last Housing Event"
            options={selectOptionsTimeSinceHousingEvent}
          />
        </SearchableField>
      </RffRowGroup>
    </Flx>
  </Flx>
);

const EditNotesFields = () => (
  <RffRowGroup>
    <SearchableField
      label="Additional Borrower Notes"
      name="additional_borrower_notes"
      keywords="notes"
    >
      <RffTextareaField
        minHeight={100}
        label="Additional Borrower Notes"
        row
        name="additional_borrower_notes"
      />
    </SearchableField>
  </RffRowGroup>
);

const BORROWER_SECTION_LABELS = {
  CONTACT: [
    "First Name",
    "Middle Name",
    "Last Name",
    "Email Address",
    "Phone",
    "Date of Birth",
    "Marital Status",
    "Citizenship Status",
    "ID Type",
    "ID Expiration Date",
    "Has a US issued SSN",
    "Social Security Number",
    "Company",
    "telephone",
    "ssn",
    "passport",
    "driver license",
  ],
  ADDRESS: [
    "Borrower Primary Address",
    "Enter non-US address",
    "street",
    "city",
    "state",
    "zip",
  ],
  CREDIT: [
    "Estimated FICO Range",
    "Estimated Numeric FICO",
    "FICO Low",
    "FICO Mid",
    "FICO High",
    "Credit Report Date",
    "Credit Auth Date",
    "Background Check Date",
    "Mortgage Lates",
    "credit score",
    "delinquency",
  ],
  ROLE: [
    "Entity Ownership %",
    "Is a guarantor on the loan",
    "Is primary contact for the loan",
    "Is a repeat borrower",
    "Role",
    "guarantor",
    "primary contact",
    "repeat",
    "ownership",
  ],
  LIQUIDITY: [
    "Total cash liquidity",
    "Total value of other liquidity sources",
    "Verified Liquidity",
    "cash",
    "other liquidity",
    "401k",
    "ira",
  ],
  EXPERIENCE: [
    "Experienced Borrower",
    "Fix & Flips",
    "Income Producing Properties",
    "Ground Up Construction",
    "Verified Experience Total",
    "Summary of borrower property investor experience",
    "is experienced",
    "rental",
  ],
  BACKGROUND: [
    "Been convicted of a felony?",
    "Been convicted of any financial or fraud related crimes including misdemeanors?",
    "Filed for bankruptcy in the last 5 years?",
    "Time Since Bankruptcy (years)",
    "Charge offs, federal or state tax liens, pending litigation or financial judgments",
    "Charge Offs",
    "Tax Liens",
    "Financial Judgements",
    "Pending Litigation",
    "Ever owned a primary residence?",
    "Housing events",
    "Time Since Last Housing Event",
    "criminal",
    "felony",
    "fraud",
    "bankruptcy",
    "liens",
    "first time homebuyer",
  ],
  NOTES: ["Additional Borrower Notes", "notes"],
};

/* ═══════════════════════════════════════════
   EDIT MODE — Renders the form with only the
   relevant section(s) based on editSection
   ═══════════════════════════════════════════ */
const BorrowerEditForm = ({
  borrower,
  loanId,
  editSection,
  onClose,
  onSuccess,
}) => {
  const { updateBorrower } = useUnderwritingHook();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [search, setSearch] = useState("");

  const handleSubmit = useCallback(
    (changedValues) => {
      setLoading(true);
      updateBorrower({
        borrowerId: borrower?._id,
        loanId: loanId,
        data: changedValues,
        onSuccessFn: (response) => {
          setLoading(false);
          onSuccess?.(response);
        },
        onErrorFn: () => {
          setLoading(false);
        },
      });
    },
    [borrower?._id, loanId, updateBorrower, onSuccess, dispatch],
  );

  const showSection = useCallback(
    (section) => {
      return editSection === EDIT_SECTIONS.ALL || editSection === section;
    },
    [editSection],
  );

  return (
    <RffFormChanges
      key={formKey}
      initialValues={borrower}
      onSubmit={handleSubmit}
      sendWholeObject
      loading={loading}
      suppressFooter
    >
      <EditSearchProvider value={search}>
        <Flx
          column
          sx={{
            px: 1.5,
            py: 1.5,
            gap: 1,
            bgcolor: "#f1f2f5",
            padding: "16px",
            borderBottom: `1px solid rgb(100 116 139 / 9%)`,
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Flx ac jb>
            <SidebarSectionTitle primary icon={<EditOutlined />}>
              Edit Borrower
            </SidebarSectionTitle>

            <Flx gap={1}>
              <Button
                color="gray"
                variant="outlined"
                onClick={() => {
                  setFormKey((prev) => prev + 1);
                  setSearch("");
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Box sx={{ background: "#ffffff", borderRadius: "4px" }}>
                <Button loading={loading} loadingPosition="end" type={"submit"}>
                  Save Changes
                </Button>
              </Box>
            </Flx>
          </Flx>
          <SearchInput
            variant="paper"
            rounded
            placeholder="Search fields..."
            fullWidth
            onClear={() => setSearch("")}
            value={search}
            onChange={setSearch}
            sx={{
              input: {
                fontSize: "12px",
                height: "14px",
                px: 0.5,
                py: 1,
              },
              svg: { fontSize: "16px !important" },
            }}
          />
        </Flx>

        <ContentArea>
          <Flx
            column
            g={0}
            sx={{
              // Lock each row to a 2-column split (label | value)
              ".input-row-root": {
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) !important",
                alignItems: "stretch",
                border: "none !important",
                borderBottom: "1px dashed #dde0e4b5 !important",
                paddingBottom: "0 !important",
              },
              // Hide the wrapper borders/padding inside the value side
              ".input-row-value-root .rff-input-row, .input-row-value-root .input-row-root, .input-row-value-root .input-wrapper-root":
                {
                  border: "none !important",
                  paddingBottom: "0 !important",
                },
              // Hide MUI input fieldset border so inputs blend into the blue cell
              ".input-row-value-root .MuiOutlinedInput-notchedOutline, .input-row-value-root fieldset":
                {
                  border: "none !important",
                },
              // Light-blue value cell (matches Property editor)
              ".input-row-value-root": {
                background: "#e3f2fd35",
                border: "1px solid #e3f2fdff",
                borderRadius: "2px",
                minHeight: 34,
                px: 1,
                py: "2px",
                width: "100%",
                boxSizing: "border-box",
                justifyContent: "flex-start",
              },
              // Input + placeholder match label font size
              ".input-row-value-root input, .input-row-value-root .MuiInputBase-input, .input-row-value-root .MuiSelect-select, .input-row-value-root .MuiAutocomplete-input":
                {
                  fontSize: "11.5px !important",
                },
              ".input-row-value-root input::placeholder, .input-row-value-root textarea::placeholder, .input-row-value-root .MuiInputBase-input::placeholder":
                {
                  fontSize: "11.5px !important",
                  opacity: 1,
                },
              // Checkboxes off the edges
              ".input-row-value-root .MuiFormControlLabel-root": {
                margin: 0,
                paddingLeft: "8px",
              },
              ".input-row-value-root .MuiCheckbox-root": {
                padding: "4px 6px",
              },
            }}
          >
            {showSection(EDIT_SECTIONS.CONTACT) && (
              <SearchableSection
                title="Contact & Identity"
                labels={BORROWER_SECTION_LABELS.CONTACT}
              >
                <SidebarSection
                  underlinePosition="body"
                  title="Contact & Identity"
                >
                  <EditContactFields />
                </SidebarSection>
              </SearchableSection>
            )}
            {showSection(EDIT_SECTIONS.ADDRESS) && (
              <SearchableSection
                title="Primary Address"
                labels={BORROWER_SECTION_LABELS.ADDRESS}
              >
                <SidebarSection
                  underlinePosition="body"
                  title="Primary Address"
                >
                  <EditAddressFields />
                </SidebarSection>
              </SearchableSection>
            )}
            {showSection(EDIT_SECTIONS.CREDIT) && (
              <SearchableSection
                title="Credit Information"
                labels={BORROWER_SECTION_LABELS.CREDIT}
              >
                <SidebarSection
                  underlinePosition="body"
                  title="Credit Information"
                >
                  <EditCreditFields />
                </SidebarSection>
              </SearchableSection>
            )}
            {showSection(EDIT_SECTIONS.ROLE) && (
              <SearchableSection
                title="Loan Role & Ownership"
                labels={BORROWER_SECTION_LABELS.ROLE}
              >
                <SidebarSection
                  underlinePosition="body"
                  title="Loan Role & Ownership"
                >
                  <EditRoleFields />
                </SidebarSection>
              </SearchableSection>
            )}
            {showSection(EDIT_SECTIONS.LIQUIDITY) && (
              <SearchableSection
                title="Liquidity"
                labels={BORROWER_SECTION_LABELS.LIQUIDITY}
              >
                <SidebarSection underlinePosition="body" title="Liquidity">
                  <EditLiquidityFields />
                </SidebarSection>
              </SearchableSection>
            )}
            {showSection(EDIT_SECTIONS.EXPERIENCE) && (
              <SearchableSection
                title="Experience"
                labels={BORROWER_SECTION_LABELS.EXPERIENCE}
              >
                <SidebarSection underlinePosition="body" title="Experience">
                  <EditExperienceFields />
                </SidebarSection>
              </SearchableSection>
            )}
            {showSection(EDIT_SECTIONS.BACKGROUND) && (
              <SearchableSection
                title="Background & Housing Events"
                labels={BORROWER_SECTION_LABELS.BACKGROUND}
              >
                <SidebarSection
                  underlinePosition="body"
                  title="Background & Housing Events"
                >
                  <EditBackgroundFields />
                </SidebarSection>
              </SearchableSection>
            )}
            {showSection(EDIT_SECTIONS.NOTES) && (
              <SearchableSection
                title="Notes"
                labels={BORROWER_SECTION_LABELS.NOTES}
              >
                <SidebarSection underlinePosition="body" title="Notes">
                  <EditNotesFields />
                </SidebarSection>
              </SearchableSection>
            )}
          </Flx>
        </ContentArea>
      </EditSearchProvider>
    </RffFormChanges>
  );
};

/* ═══════════════════════════════════════════
   DETAILS SECTION COMPONENTS
   ═══════════════════════════════════════════ */

const ContactIdentitySection = ({ borrower, quickFilter, onOpenAddress }) => {
  const fields = useMemo(
    () => [
      {
        label: "First Name",
        name: "firstName",
        editor: { type: "text" },
        value: borrower?.firstName,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "first name given legal",
      },
      {
        label: "Middle Name",
        name: "middleName",
        editor: { type: "text" },
        value: borrower?.middleName,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "middle name legal",
      },
      {
        label: "Last Name",
        name: "lastName",
        editor: { type: "text" },
        value: borrower?.lastName,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "last name surname family legal",
      },
      {
        label: "Email",
        name: "emailAddress",
        editor: { type: "text" },
        value: borrower?.emailAddress,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "email address contact",
      },
      {
        label: "Phone",
        name: "phone",
        editor: { type: "phone" },
        value: borrower?.phone ? formatPhone(borrower.phone) : null,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "phone number telephone contact",
      },
      {
        label: "Address",
        value: addressLabel(borrower?.primaryAddress),
        fieldType: FIELD_TYPE.READ_ONLY,
        searchTerms: "address primary home residence street",
        // Locked here — the address has its own editor on the Address sub-tab.
        // Clicking the value takes you there rather than dead-ending.
        onValueClick: onOpenAddress,
        valueClickHint: "Edit on the Address tab",
      },
      {
        label: "Date of Birth",
        name: "dateOfBirth",
        editor: { type: "date" },
        value: fmtDate(borrower?.dateOfBirth),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "date of birth dob birthday age",
      },
      {
        label: "SSN",
        name: "borrowerSSN",
        editor: { type: "ssn" },
        value: borrower?.borrowerSSN,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "ssn social security number",
      },
      {
        label: "Citizenship",
        name: "citizenshipStatus",
        editor: { type: "select", options: selectOptionsCitizenshipStatus },
        value: borrower?.citizenshipStatus,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "citizenship status citizen foreign national",
      },
      {
        label: "Marital Status",
        name: "maritalStatus",
        editor: { type: "select", options: selectOptionsMaritalStatus },
        value: borrower?.maritalStatus,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "marital status married",
      },
      {
        label: "ID Type",
        name: "borrowerIdType",
        editor: { type: "select", options: selectOptionsIdType },
        value: borrower?.borrowerIdType,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "id type identification driver license passport",
      },
      {
        label: "ID Expiration",
        name: "idExpirationDate",
        editor: { type: "date" },
        value: fmtDate(borrower?.idExpirationDate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "id expiration date identification",
      },
      {
        label: "Company",
        name: "company",
        editor: { type: "text" },
        value: borrower?.company,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "company entity name business",
      },
      {
        label: "Foreign National",
        value: borrower?.foreignNationalFlag ? "Yes" : "No",
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "foreign national flag citizenship computed",
        computedTooltip:
          "Auto-calculated: True when citizenship status is not 'US Citizen'",
      },
    ],
    [borrower, onOpenAddress],
  );

  return (
    <FieldsSection
      title="Contact & Identity"
      icon={<PersonOutlineOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

const CreditBackgroundSection = ({ borrower, quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        label: "FICO Range",
        name: "estimatedFICO",
        editor: { type: "select", options: selectOptionsEstimatedFico },
        value: borrower?.estimatedFICO,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "fico range estimated credit score",
      },
      {
        label: "FICO Low",
        name: "ficoLow",
        editor: { type: "number" },
        value: borrower?.ficoLow,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "fico low credit score",
      },
      {
        label: "FICO Mid",
        name: "ficoMid",
        editor: { type: "number" },
        value: borrower?.ficoMid,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "fico mid credit score",
      },
      {
        label: "FICO Max",
        name: "ficoMax",
        editor: { type: "number" },
        value: borrower?.ficoMax,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "fico high max credit score",
      },
      {
        label: "Credit Report Date",
        name: "creditReportDate",
        editor: { type: "date" },
        value: fmtDate(borrower?.creditReportDate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "credit report date",
      },
      {
        label: "Credit Auth Date",
        name: "creditAuthDate",
        editor: { type: "date" },
        value: fmtDate(borrower?.creditAuthDate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "credit authorization auth date",
      },
      {
        label: "Background Check",
        name: "backgroundDate",
        editor: { type: "date" },
        value: fmtDate(borrower?.backgroundDate),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "background check date",
      },
      {
        label: "Mortgage Lates",
        name: "mortgage_lates",
        editor: { type: "selectMultiple", options: mortgageLateSelections },
        // mortgage_lates is sometimes an array, sometimes a scalar (string /
        // number) depending on the upstream source — guard before .join so a
        // scalar value doesn't blow up the whole sidebar.
        value: Array.isArray(borrower?.mortgage_lates)
          ? borrower.mortgage_lates.join(", ") || "None"
          : (borrower?.mortgage_lates ?? "None"),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "mortgage lates late payments delinquency",
      },
      {
        label: "Credit Decision Score",
        value: borrower?.rtlCreditDecisionScore,
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "credit decision score rtl computed",
        computedTooltip:
          "Auto-calculated from FICO mid score: ≥700 → 3, 680-699 → 1, 660-679 → 0, Foreign national → 0, Below minimum → -1",
      },
    ],
    [borrower],
  );

  return (
    <FieldsSection
      title="Credit & Scores"
      icon={<CreditScoreOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

const LoanRoleSection = ({ borrower, quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        label: "Entity Ownership",
        name: "entityPercentOwnership",
        editor: { type: "percent" },
        value: pct(borrower?.entityPercentOwnership),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "entity ownership percent",
      },
      {
        label: "Guarantor",
        name: "isGuarantor",
        editor: { type: "boolean" },
        value: borrower?.isGuarantor ? "Yes" : "No",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "guarantor",
      },
      {
        label: "Primary Contact",
        name: "isPrimaryContactForLoan",
        editor: { type: "boolean" },
        value: borrower?.isPrimaryContactForLoan ? "Yes" : "No",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "primary contact loan",
      },
      {
        label: "Repeat Borrower",
        name: "repeatBorrowerFlag",
        editor: { type: "boolean" },
        value: borrower?.repeatBorrowerFlag
          ? "Yes"
          : borrower?.repeatBorrowerFlag === false
            ? "No"
            : "—",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "repeat borrower flag",
      },
      {
        label: "Role",
        name: "role",
        editor: { type: "text" },
        value: borrower?.role,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "role borrower broker correspondent",
      },
    ],
    [borrower],
  );

  return (
    <FieldsSection
      title="Loan Role & Ownership"
      icon={<BadgeOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

const LiquiditySection = ({ borrower, quickFilter }) => {
  const userEmail = useSelector((s) => s.user?.emailAddress);
  // verifiedLiquidity is UW-owned: editable only for users in the underwriter
  // or admin rosters; otherwise shown read-only.
  const canEditUw = emailInRoles(userEmail, ["underwriter", "admin"]);
  const totalLiquidity =
    (borrower?.cashLiquidity || 0) + (borrower?.otherLiquidity || 0);

  const fields = useMemo(
    () => [
      {
        label: "Cash Liquidity",
        name: "cashLiquidity",
        editor: { type: "dollar" },
        value: fmt(borrower?.cashLiquidity),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "cash liquidity bank accounts",
      },
      {
        label: "Other Liquidity",
        name: "otherLiquidity",
        editor: { type: "dollar" },
        value: fmt(borrower?.otherLiquidity),
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "other liquidity sources",
      },
      {
        label: "Total Liquidity",
        value: fmt(totalLiquidity),
        fieldType: FIELD_TYPE.COMPUTED,
        searchTerms: "total liquidity combined computed",
        computedTooltip: "Auto-calculated: Cash Liquidity + Other Liquidity",
      },
      {
        label: "Verified Liquidity",
        name: "verifiedLiquidity",
        editor: { type: "dollar" },
        value: fmt(borrower?.verifiedLiquidity),
        fieldType: canEditUw ? FIELD_TYPE.EDITABLE : FIELD_TYPE.READ_ONLY,
        searchTerms: "verified liquidity underwriting",
        infoTooltip:
          "the verified liquidity of the borrower based on bank statements or other documentation based on underwriting",
      },
    ],
    [borrower, totalLiquidity, canEditUw],
  );

  return (
    <FieldsSection
      title="Liquidity"
      icon={<AccountBalanceWalletOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

/** Search terms the category checkboxes stand in for (see anyFieldsVisible). */
const EXPERIENCE_EDITOR_SEARCH = {
  label: "Self-Reported Experience Summary",
  searchTerms:
    "fix flip sold retained rental income producing properties ground up construction general contractor rehab experience counts self reported",
};

/* ── Calculated from Verified Experience Total ──────────────────────────
   Experience Score + Borrower Classification, derived client-side.

   The backend exposes these only as plain `@property`s on the Borrower model
   (never serialized), so the loan JSON never carries them. This mirrors the
   pricing engine's classification instead — `pricing_structures.py
   classify()` with its default tables — which is the scoring that actually
   drives pricing, and it keys off the SAME inputs shown above:
     • experience  = verifiedExperienceTotal (deal count)  → 10+ → 7, 3–9 → 5, 0–2 → 1
     • credit      = ficoMid                                → 700+ → 3, 680–699 → 1, else / FN → 0
     • class       = credit + experience                    → ≥7 A+, ≥5 A, ≥2 B, else C
   Ground-up prices off its own thresholds (A+ ≥4, A ≥2, B ≥1 with FICO 700+),
   where the score IS the deal count.
   If the engine's tables ever change, this drifts — the durable fix is a
   backend `@computed_field` on the model. */
const GROUND_UP_PRODUCT = "Ground Up Construction";

const computeExperienceScoring = (borrower, loanProductType) => {
  const deals = Number(borrower?.verifiedExperienceTotal) || 0;
  const fico = Number(borrower?.ficoMid) || 0;
  const isFn = Boolean(borrower?.foreignNationalFlag);
  if (loanProductType === GROUND_UP_PRODUCT) {
    const f = isFn ? 0 : fico;
    let cls = "C";
    if (deals >= 4) cls = "A+";
    else if (deals >= 2) cls = "A";
    else if (deals >= 1 && f >= 700) cls = "B";
    return { experienceScore: Math.trunc(deals), creditScore: f, classification: cls };
  }
  const creditScore = isFn ? 0 : fico >= 700 ? 3 : fico >= 680 ? 1 : 0;
  const experienceScore = deals >= 10 ? 7 : deals >= 3 ? 5 : 1;
  const total = creditScore + experienceScore;
  const classification =
    total >= 7 ? "A+" : total >= 5 ? "A" : total >= 2 ? "B" : "C";
  return { experienceScore, creditScore, classification };
};

const ComputedExperienceBlock = ({ borrower }) => {
  const loanProductType = useSelector(
    (s) => s.loanDrilldown?.loanProductType,
  );
  const { experienceScore, creditScore, classification } = useMemo(
    () => computeExperienceScoring(borrower, loanProductType),
    [borrower, loanProductType],
  );
  const isGroundUp = loanProductType === GROUND_UP_PRODUCT;
  const deals = Number(borrower?.verifiedExperienceTotal) || 0;

  const rows = [
    {
      label: "Experience Score",
      value: experienceScore,
      help: isGroundUp
        ? `Ground-up: the score is the verified deal count itself (${deals}).`
        : `From ${deals} verified deal${deals === 1 ? "" : "s"}: 10+ → 7, 3–9 → 5, 0–2 → 1.`,
    },
    {
      label: "Borrower Classification",
      value: classification,
      help: isGroundUp
        ? "Ground-up thresholds on verified deals: 4+ → A+, 2–3 → A, 1 → B (needs FICO 700+), else C."
        : `Credit score ${creditScore} (FICO Mid: 700+ → 3, 680–699 → 1, else 0) + experience score ${experienceScore} = ${creditScore + experienceScore}. ≥7 → A+, ≥5 → A, ≥2 → B, else C.`,
    },
  ];

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 1.5,
        borderRadius: "10px",
        border: `1px solid ${teal[150]}`,
        background: teal[50],
        overflow: "hidden",
      }}
    >
      <Flx
        ac
        jb
        g={1}
        sx={{ px: 1.5, py: 0.9, borderBottom: `1px solid ${teal[150]}` }}
      >
        <Flx ac g={0.75}>
          <SigmaIcon sx={{ fontSize: 14, color: teal[700] }} />
          <Txt sx={{ fontSize: 12, fontWeight: 600, color: teal[700] }}>
            Calculated from Verified Experience Total
          </Txt>
        </Flx>
        <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: teal[700] }}>
          Auto
        </Txt>
      </Flx>
      {rows.map((row, i) => (
        <Flx
          key={row.label}
          ac
          jb
          g={1}
          sx={{
            px: 1.5,
            py: 1.1,
            borderBottom: i < rows.length - 1 ? `1px solid ${teal[150]}` : "none",
          }}
        >
          <Flx ac g={0.5}>
            <Txt sx={{ fontSize: 12, color: slate[600] }}>{row.label}</Txt>
            <Tooltip title={row.help} arrow placement="left">
              <HelpOutline
                sx={{ fontSize: 14, color: slate[400], cursor: "help" }}
              />
            </Tooltip>
          </Flx>
          <Flx ac g={0.5}>
            <SigmaIcon sx={{ fontSize: 13, color: teal[700] }} />
            <Txt sx={{ fontSize: 12, fontWeight: 700, color: teal[700] }}>
              {row.value}
            </Txt>
          </Flx>
        </Flx>
      ))}
    </Box>
  );
};

const ExperienceSection = ({ borrower, quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        label: "Verified Experience Total",
        name: "verifiedExperienceTotal",
        editor: { type: "float" },
        value: `${fmtPoints(borrower?.verifiedExperienceTotal)} pts`,
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "verified experience total deals",
        infoTooltip:
          "Points from verified experience-workbook items crediting this borrower. A full project counts 1.0; an income-producing purchase or third-party GC work counts 0.5.",
      },
      // The three hasExperienceWith* flags used to sit here as Yes/No rows.
      // They're now the category checkboxes in BorrowerExperienceEditor below —
      // keeping both would mean two controls writing one field in one card.
      {
        label: "Experienced Borrower",
        name: "isExperienced",
        editor: { type: "boolean" },
        value: borrower?.isExperienced ? "Yes" : "No",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "experienced borrower",
      },
    ],
    [borrower],
  );
  // Experience Score / Borrower Classification sit UNDER the inputs as a
  // calculated block (ComputedExperienceBlock) rather than as rows — they're
  // derived from the rows above, not entered.

  // Hide both the supplementary cards when the field-level filter has zeroed
  // out the Experience card — mirrors the previous all-or-nothing behavior.
  // EXPERIENCE_EDITOR_SEARCH carries the terms the three hasExperienceWith*
  // rows used to match on; without it, searching "fix flip" would hide the very
  // card that now edits it.
  const anyFieldsVisible = useMemo(
    () =>
      fields.some((f) => fieldPassesSearch(f, quickFilter)) ||
      fieldPassesSearch(EXPERIENCE_EDITOR_SEARCH, quickFilter),
    [fields, quickFilter],
  );
  if (!anyFieldsVisible) return null;

  return (
    <>
      <FieldsSection
        title="Experience"
        icon={<BusinessCenterOutlined />}
        fields={fields}
        quickFilter={quickFilter}
        // No hairlines between the two inputs — the calculated block below
        // already frames the card, and the rules read as underlines on it.
        sx={{ "& .panel-row-frame": { borderBottom: "none" } }}
      >
        <ComputedExperienceBlock borrower={borrower} />
      </FieldsSection>
      <PanelCard
        title="Self-Reported Experience Summary"
        subTitle="Borrower-reported. Select the categories that apply, then enter the counts."
        icon={<BusinessCenterOutlined />}
        variant="loanInfo"
      >
        <BorrowerExperienceEditor />
      </PanelCard>
      {/* Only meaningful once a portal account is linked — the records come
          from that account and there's no way to add them here, so an empty
          card on an unlinked borrower is just noise. */}
      {borrower?.borrowerPortalUserId && (
        <PanelCard
          title="Validated Properties"
          subTitle="Synced from the borrower's Portal account — read-only here. Edit them in the portal, then re-sync from the Overview tab."
          icon={<HowToRegOutlined />}
          variant="loanInfo"
        >
          <ValidatedExperienceList experience={borrower?.validatedExperience} />
        </PanelCard>
      )}
    </>
  );
};

const HousingEventsSection = ({ borrower, quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        label: "Housing Events",
        name: "housing_events",
        editor: { type: "selectMultiple", options: selectOptionsHousingEvents },
        value:
          Array.isArray(borrower?.housing_events) &&
          borrower.housing_events.length > 0
            ? borrower.housing_events.join(", ")
            : "None",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms:
          "housing events forbearance foreclosure deed lis pendens mortgage lates",
      },
      {
        label: "Time Since Event",
        name: "time_since_housing_event",
        editor: {
          type: "select",
          options: selectOptionsTimeSinceHousingEvent,
        },
        value: borrower?.time_since_housing_event
          ? `${borrower.time_since_housing_event}`
          : "N/A",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "time since housing event years",
      },
      {
        label: "Time Since Bankruptcy",
        name: "time_since_bankruptcy",
        editor: { type: "text" },
        value: borrower?.time_since_bankruptcy
          ? `${borrower.time_since_bankruptcy} yrs`
          : "N/A",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "time since bankruptcy years",
      },
      {
        label: "Bankruptcy Flag",
        name: "bankruptcy_flag",
        editor: { type: "boolean" },
        value: borrower?.bankruptcy_flag ? "Yes" : "No",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "bankruptcy flag",
      },
      {
        label: "Felony Conviction",
        name: "application_felony_conviction",
        editor: { type: "boolean" },
        value: borrower?.application_felony_conviction ? "Yes" : "No",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "felony conviction criminal",
      },
      {
        label: "Financial Fraud",
        name: "application_financial_fraud_misdemeanor",
        editor: { type: "boolean" },
        value: borrower?.application_financial_fraud_misdemeanor ? "Yes" : "No",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms: "financial fraud misdemeanor criminal",
      },
      {
        label: "Open Liens / Judgments",
        name: "open_liens_judgments",
        editor: { type: "boolean" },
        value: borrower?.open_liens_judgments ? "Yes" : "No",
        fieldType: FIELD_TYPE.EDITABLE,
        searchTerms:
          "open liens judgments charge offs tax liens pending litigation",
      },
      ...(borrower?.open_liens_judgments
        ? [
            {
              label: "Charge Offs",
              name: "charge_offs",
              editor: { type: "boolean" },
              value: borrower?.charge_offs ? "Yes" : "No",
              fieldType: FIELD_TYPE.EDITABLE,
              searchTerms: "charge offs",
            },
            {
              label: "Tax Liens",
              name: "tax_liens",
              editor: { type: "boolean" },
              value: borrower?.tax_liens ? "Yes" : "No",
              fieldType: FIELD_TYPE.EDITABLE,
              searchTerms: "tax liens",
            },
            {
              label: "Financial Judgements",
              name: "financial_judgements",
              editor: { type: "boolean" },
              value: borrower?.financial_judgements ? "Yes" : "No",
              fieldType: FIELD_TYPE.EDITABLE,
              searchTerms: "financial judgements",
            },
            {
              label: "Pending Litigation",
              name: "pending_litigation",
              editor: { type: "boolean" },
              value: borrower?.pending_litigation ? "Yes" : "No",
              fieldType: FIELD_TYPE.EDITABLE,
              searchTerms: "pending litigation",
            },
          ]
        : []),
    ],
    [borrower],
  );

  return (
    <FieldsSection
      title="Housing Events & History"
      icon={<HistoryEduOutlined />}
      fields={fields}
      quickFilter={quickFilter}
    />
  );
};

const NotesSection = ({ borrower, quickFilter }) => {
  const passesSearch = useMemo(() => {
    if (!quickFilter || quickFilter.trim() === "") return true;
    const q = quickFilter.toLowerCase();
    const notes = (borrower?.additional_borrower_notes || "").toLowerCase();
    const summary = (borrower?.borrowerExperienceSummary || "").toLowerCase();
    return (
      "notes additional borrower experience summary".includes(q) ||
      notes.includes(q) ||
      summary.includes(q)
    );
  }, [
    quickFilter,
    borrower?.additional_borrower_notes,
    borrower?.borrowerExperienceSummary,
  ]);

  if (!passesSearch) return null;

  return (
    <PanelCard title="Notes" icon={<NotesOutlined />} variant="loanInfo">
      <Box sx={{ py: 1 }}>
        <Txt sx={{ fontSize: 11, color: grey[500], fontWeight: 500, mb: 0.5 }}>
          Borrower experience summary
        </Txt>
        <RffTextareaField
          name="borrowerExperienceSummary"
          label={null}
          minHeight={80}
          suppressGrid
        />
      </Box>
      <Box sx={{ py: 1 }}>
        <Txt sx={{ fontSize: 11, color: grey[500], fontWeight: 500, mb: 0.5 }}>
          Additional borrower notes
        </Txt>
        <RffTextareaField
          name="additional_borrower_notes"
          label={null}
          minHeight={100}
          suppressGrid
        />
      </Box>
    </PanelCard>
  );
};
const OpenLocationButton = ({ lat, lng, address, sx = {} }) => {
  const hasCoords = lat != null && lng != null;
  const hasAddress = Boolean(address);

  const handleClick = () => {
    let url = "";
    if (hasCoords) {
      url = `https://www.google.com/maps?q=${lat},${lng}`;
    } else if (hasAddress) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Tooltip title="Open in Google Maps">
      <IconButton
        onClick={handleClick}
        disabled={!hasCoords && !hasAddress}
        sx={{
          border: `1px solid ${grey[300]}`,
          zIndex: 11,
          "&:hover": { background: "#ffffff" },
          ...sx,
        }}
      >
        <OpenInNew classrow Name="thin" />
      </IconButton>
    </Tooltip>
  );
};
// RTL grade → KPI tone. A/A+ are strong, B is caution, C/D are risk.
const classificationTone = (grade) => {
  const g = String(grade || "").toUpperCase();
  if (g === "A+" || g === "A") return "green";
  if (g === "B") return "amber";
  if (g === "C" || g === "D") return "red";
  return "neutral";
};

// Priority KPI tiles for a borrower, ordered by decision relevance per
// BORROWER_PRIORITY_FIELDS.md: credit (FICO) → RTL grade → verified
// experience → liquidity (verified, with claimed total as context).
// Verified figures lead; claimed/self-reported show as subtitle context.
// All computed values degrade gracefully when the rtl* scores are absent.
const MetricCardsRow = ({ borrower }) => {
  // Liquidity: verified (proven from docs) is the decision figure; the
  // claimed total is cash + other liquidity, shown as subtitle context.
  const claimedLiquidity =
    (borrower?.cashLiquidity || 0) + (borrower?.otherLiquidity || 0);
  const verifiedLiquidity = Number(borrower?.verifiedLiquidity) || 0;
  // Verified / validated experience leads; falls back to the count of
  // validatedExperience records when the rolled-up total isn't present.
  const verifiedExp =
    Number(borrower?.verifiedExperienceTotal) ||
    size(borrower?.validatedExperience) ||
    0;
  // FICO mid is the decisioning score; fall back to an estimated score
  // when no bureau pull has happened yet.
  const ficoMid = borrower?.ficoMid;
  const estFico =
    borrower?.estimatedNumericFICO || borrower?.estimatedFICO || null;
  const usingEstFico = ficoMid == null && estFico != null;
  const ficoValue = ficoMid ?? estFico ?? null;
  const grade = borrower?.rtlBorrowerClassification;

  const metrics = [
    {
      key: "fico",
      label: "FICO (Mid)",
      value: ficoValue ?? "—",
      subtitle: usingEstFico ? "Estimated" : undefined,
      valueColor: ficoColor(ficoValue),
    },
    // RTL grade is best-effort (model gotcha §11.5) — only render when the
    // classification actually computed, and always alongside the raw
    // inputs (FICO / experience tiles) rather than on its own.
    ...(grade
      ? [
          {
            key: "grade",
            label: "RTL Grade",
            value: grade,
            tone: classificationTone(grade),
          },
        ]
      : []),
    {
      key: "verified-exp",
      label: "Verified Exp.",
      value: verifiedExp,
      subtitle: "verified deals",
      tone: verifiedExp > 0 ? "green" : "neutral",
    },
    {
      key: "liquidity",
      label: "Liquidity",
      // Primary = total (claimed) liquidity; verified shown smaller below.
      value: formatDollar(claimedLiquidity),
      subtitle: `${formatDollar(verifiedLiquidity)} verified`,
      tone: verifiedLiquidity > 0 ? "green" : "neutral",
    },
  ];
  return (
    <Flx g={1} sx={{ flexWrap: "wrap" }}>
      {metrics.map((m) => (
        <KpiWidget
          sx={{ flex: "1 1 120px" }}
          key={m.key || m.label}
          label={m.label}
          subtitle={m.subtitle}
          tone={m.tone}
          suppressBoxShadow
          value={m.value}
          valueColor={m.valueColor}
        />
      ))}
    </Flx>
  );
};

/* ═══════════════════════════════════════════
   MAIN SIDEBAR COMPONENT
   ═══════════════════════════════════════════ */
const LoanBorrowerSidebar = ({ borrower, loan }) => {
  const loanId = loan?._id;
  const dispatch = useDispatch();
  const [primaryTab, setPrimaryTab] = useState(TABS.APPLICANT_INFO);
  // External tab control — the redesign-tab walkthrough writes a tab
  // name to `detailSidebar.meta.forceTab` to drive the sidebar through
  // Overview → Applicant Info → Files → Comments. Anywhere else can do
  // the same via `detailSidebarUpdateMeta({ forceTab: "Files" })`.
  const forceTabFromMeta = useSelector(
    (state) => state.detailSidebar?.meta?.forceTab,
  );
  useEffect(() => {
    if (!forceTabFromMeta) return;
    if (!Object.values(TABS).includes(forceTabFromMeta)) return;
    if (forceTabFromMeta === primaryTab) return;
    setPrimaryTab(forceTabFromMeta);
    // We intentionally don't depend on `primaryTab` — only on the
    // external signal — so the user can manually click away to another
    // tab after the tour drove it without us slamming it back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceTabFromMeta]);
  const [borrowerTab, setBorrowerTab] = useState(BORROWER_TABS.ALL);
  const [commentKey, setCommentKey] = useState(0);
  const [quickFilter, setQuickFilter] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const { getCommentsAssociatedWithBorrower } = useCommentUtilsHook();
  const { deleteBorrower, updateBorrower } = useUnderwritingHook();

  const handleInlineSave = useCallback(
    (changedValues) => {
      if (!borrower?._id) return;
      setSaving(true);
      updateBorrower({
        borrowerId: borrower._id,
        loanId,
        data: changedValues,
        onSuccessFn: (response) => {
          dispatch(loanDrilldownSet(response));
          dispatch(loanPipelineClear(response));
        },
        onFailFn: () => setSaving(false),
        onCompleteFn: () => setSaving(false),
      });
    },
    [borrower?._id, loanId, updateBorrower, dispatch],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!loanId || !borrower?._id) return;
    setDeleting(true);
    deleteBorrower({
      loanId,
      borrowerId: borrower._id,
      onSuccessFn: (updatedLoan) => {
        dispatch(loanDrilldownSet(updatedLoan));
        dispatch(detailSidebarClear());
        setDeleting(false);
        setConfirmDelete(false);
      },
      onFailFn: () => {
        setDeleting(false);
      },
    });
  }, [loanId, borrower?._id, deleteBorrower, dispatch]);
  const { getAssociationDocumentsByBorrowerId } = useAssociationUtils();

  const borrowerFiles = useMemo(() => {
    return (
      getAssociationDocumentsByBorrowerId(loan, borrower?._id) || []
    ).filter((f) => !isDocHidden(f));
  }, [loan, borrower, getAssociationDocumentsByBorrowerId]);

  // Count of loan documents the borrower uploaded themselves — i.e. docs
  // whose `uploadedBy` matches the borrower's email (case-insensitive).
  // Surfaced in the Borrower Portal card as a count widget.
  const portalUploadCount = useMemo(() => {
    const email = (borrower?.emailAddress || "").trim().toLowerCase();
    if (!email) return 0;
    return (loan?.loanDocuments || []).filter(
      (d) => (d?.uploadedBy || "").trim().toLowerCase() === email,
    ).length;
  }, [loan?.loanDocuments, borrower?.emailAddress]);

  const borrowerComments = useMemo(() => {
    const targetId = borrower?._id;
    return getCommentsAssociatedWithBorrower(loan?.comments, targetId);
  }, [getCommentsAssociatedWithBorrower, loan?.comments, borrower?._id]);
  // ── Edit mode state ──
  const [isEditing, setIsEditing] = useState(false);
  const [editSection, setEditSection] = useState(EDIT_SECTIONS.ALL);

  const sidebarTitle = useMemo(() => {
    if (isNil(borrower)) return "Borrower";
    return `${borrower?.firstName} ${borrower?.lastName}`;
  }, [borrower]);

  const sidebarDescription = useMemo(() => {
    if (isNil(borrower)) return null;
    return `${borrower?.emailAddress || ""} | ${
      borrower?.phone ? formatPhone(borrower.phone) : "No Phone"
    }`;
  }, [borrower]);

  const handleCloseSidebar = () => {
    // Reset to the landing tab so the next applicant opens where this one did.
    setPrimaryTab(TABS.APPLICANT_INFO);
    setBorrowerTab(BORROWER_TABS.ALL);
    setQuickFilter("");
    setIsEditing(false);
    dispatch(detailSidebarClear());
  };

  // ── Enter edit mode for a specific section or all ──
  const handleStartEdit = useCallback((section = EDIT_SECTIONS.ALL) => {
    setEditSection(section);
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditSection(EDIT_SECTIONS.ALL);
  }, []);

  const handleEditSuccess = useCallback(
    (response) => {
      dispatch(loanDrilldownSet(response));
      dispatch(loanPipelineClear());
      setIsEditing(false);
    },
    [dispatch],
  );

  // Expand/collapse all for the Information tab's cards. Only the token change
  // matters to PanelCard, so collapsing one card by hand afterwards sticks.
  const [cardSignal, setCardSignal] = useState(null);
  const signalCards = useCallback((open) => {
    setCardSignal((s) => ({ token: (s?.token || 0) + 1, open }));
  }, []);

  /** Helper: should a given borrower sub-tab section be visible? */
  const showBorrowerSection = useCallback(
    (sectionTab) => {
      return borrowerTab === BORROWER_TABS.ALL || borrowerTab === sectionTab;
    },
    [borrowerTab],
  );

  // Card layout for the Information tab — shared with the property sidebar.
  const { ref: attachMasonryRef, sx: masonrySx } = useCardMasonry(
    BORROWER_TAB_CARD_COUNTS[borrowerTab] ?? 1,
  );

  const TAB_LIST = useMemo(() => {
    return [
      // Information leads — it's the landing tab.
      { label: TABS.APPLICANT_INFO, value: TABS.APPLICANT_INFO },
      { label: TABS.OVERVIEW, value: TABS.OVERVIEW },
      { label: `${TABS.FILES} (${size(borrowerFiles)})`, value: TABS.FILES },
      { label: TABS.KEY_DOCUMENTS, value: TABS.KEY_DOCUMENTS },
      {
        label: `${TABS.COMMENTS} (${size(borrowerComments)})`,
        value: TABS.COMMENTS,
      },
      { label: TABS.JSON, value: TABS.JSON },
    ];
  }, [borrowerFiles, borrowerComments]);

  if (isNil(borrower) || isNil(loan)) {
    return null;
  }

  return (
    <ResizeableSidebar
      open
      hideBackdrop
      onClose={handleCloseSidebar}
      title={sidebarTitle}
      titleIcon={
        <Flx
          ac
          jc
          sx={{
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "#e0f2fe",
            color: "#0891b2",
            "& svg": { fontSize: 20 },
          }}
        >
          <PersonOutlineOutlined />
        </Flx>
      }
      bodySx={{
        padding: "0 !important",
        background: BODY_BACKGROUND_COLOR,
        // background:
        //   primaryTab === TABS.OVERVIEW ? "#ffffff" : BODY_BACKGROUND_COLOR,
      }}
      defaultWidth={SIDEBAR_DEFAULT_WIDTH}
      closeButtonStartContent={
        <PanelIconButton
          title="Delete Borrower"
          icon={DeleteOutlineOutlined}
          onClick={() => setConfirmDelete(true)}
        />
      }
      descriptionInline
      headerSx={{ pb: 1 }}
      hideDivider
      descriptionContent={<LoanBorrowerBadgeRow borrower={borrower} />}
      headerBottomContent={
        <SidebarTabSwitcher
          variant="underline"
          hideDivider
          sx={{ borderBottom: "1px solid #eef1f4" }}
          tabs={TAB_LIST}
          value={primaryTab}
          onChange={setPrimaryTab}
        />
      }
    >
      {/* ══════════════════════════════════════
         EDIT MODE — replaces normal tab content
         ══════════════════════════════════════ */}
      {isEditing ? (
        <SidebarBodyContent py={0} px={0}>
          <BorrowerEditForm
            borrower={borrower}
            loanId={loanId}
            editSection={editSection}
            onClose={handleCancelEdit}
            onSuccess={handleEditSuccess}
          />
        </SidebarBodyContent>
      ) : (
        <>
          {/* ══════════════════════════════════════
             VIEW MODE — normal sidebar tabs
             ══════════════════════════════════════ */}
          {/* <SidebarTabSwitcher
            tabs={TAB_LIST}
            value={primaryTab}
            onChange={setPrimaryTab}
          /> */}
          <SidebarBodyContent py={0} px={0}>
            {/* ── OVERVIEW TAB ── */}
            <TabContent tabKey={TABS.OVERVIEW} activeTab={primaryTab}>
              <ContentArea>
                <PanelCard
                  title="Summary"
                  py={2}
                  icon={<DescriptionOutlined />}
                >
                  <Flx column gap={4}>
                    {/* <Flx column gap={1}>
                      <Txt secondary>Quick Actions</Txt>
                      <LoanBorrowerSidebarActions
                        borrower={borrower}
                        dealId={loanId}
                        onEditAddress={() =>
                          handleStartEdit(EDIT_SECTIONS.ADDRESS)
                        }
                      />
                      <SidebarBodyContentDivider mt={1} mb={0.4} />
                    </Flx> */}

                    <MetricCardsRow borrower={borrower} />
                    {/* <MetricBar borrower={borrower} /> */}
                    <LoanBorrowerSidebarSummaryWidgets borrower={borrower} />
                  </Flx>
                </PanelCard>


                {/* Dedicated Borrower Portal card — link status + dates plus
                    the portal quick actions. The same actions still live in
                    the Summary card above for now (not removed yet). */}
                <PanelCard
                  title="Borrower Portal"
                  py={2}
                  icon={<AccountCircleOutlined />}
                  subTitle="The account the borrower uses to sign into the Borrower Portal."
                >
                  <Flx column gap={2}>
                    {/* Portal status — same SummaryWidget style as the
                        Summary card's widget row. */}
                    {borrower?.borrowerPortalUserId ? (
                      <Flx column>
                        {/* <Alert severity="success">Linked to Portal</Alert> */}
                        <FlxGrid width="220px" gap={2} sx={{ mb: 1.5 }}>
                          <SummaryWidget
                            checkable
                            checked={borrower?.borrowerPortalUserId}
                            label="Account Link Date"
                            checkedIcon={<LinkOutlined />}
                            checkedColor="green"
                            uncheckedColor="gray"
                            description={
                              borrower?.borrowerPortalUserId
                                ? `Linked ${formatDateTime(borrower?.portalUserLinkedDate)}`
                                : "Not linked to a portal account"
                            }
                          />
                          <SummaryWidget
                            checkable
                            checked={borrower?.lastSyncedFromPortal}
                            label="Last Synced from Portal"
                            checkedIcon={<SyncOutlined />}
                            checkedColor="green"
                            uncheckedColor="gray"
                            description={
                              borrower?.lastSyncedFromPortal
                                ? `Synced ${formatDateTime(borrower?.lastSyncedFromPortal)}`
                                : "Not linked to a portal account"
                            }
                          />
                          <SummaryWidget
                            icon={CloudUploadOutlined}
                            label="Documents from Portal"
                            count={portalUploadCount}
                            description="Uploaded by the borrower"
                            {...SUMMARY_WIDGET_THEMES.blue}
                          />
                        </FlxGrid>
                      </Flx>
                    ) : (
                      <Alert severity="warning">Not linked to Portal</Alert>
                    )}

                    <Flx column gap={1}>
                      {/* <Txt secondary>Portal Actions</Txt> */}
                      <LoanBorrowerSidebarActions
                        borrower={borrower}
                        dealId={loanId}
                        onEditAddress={() =>
                          handleStartEdit(EDIT_SECTIONS.ADDRESS)
                        }
                      />
                    </Flx>
                  </Flx>
                </PanelCard>

                <PanelCard title="Notes" py={2} icon={<DescriptionOutlined />}>
                  {isNil(borrower?.borrowerExperienceSummary) &&
                  isNil(borrower?.additional_borrower_notes) ? (
                    <Txt sx={{ opacity: 0.85 }} secondary>
                      No notes added to this applicant
                    </Txt>
                  ) : (
                    <Flx ac g={3} fw>
                      {borrower?.borrowerExperienceSummary && (
                        <DetailBox label={"Borrower Experience Summary"}>
                          {borrower?.borrowerExperienceSummary ||
                            "No notes added"}
                        </DetailBox>
                      )}
                      {borrower?.additional_borrower_notes && (
                        <DetailBox label={"General Borrower Notes"}>
                          {borrower?.additional_borrower_notes ||
                            "No notes added"}
                        </DetailBox>
                      )}
                    </Flx>
                  )}
                </PanelCard>

                <PanelCard
                  icon={<ImageOutlined />}
                  title={`Related Borrower Files (${size(borrowerFiles)})`}
                  subTitle="All files associated & linked to this borrower."
                  headerEndContent={
                    <Flx ac g={1} sx={{ mr: 0.5 }}>
                      <Button
                        onClick={() => setPrimaryTab(TABS.FILES)}
                        color={"gray"}
                        variant={"outlined"}
                        size={"small"}
                        startIcon={
                          <UploadFileOutlined
                            sx={{ fontSize: "15px !important" }}
                          />
                        }
                      >
                        Upload
                      </Button>
                      <AssignLoanFilesButton borrower={borrower}>
                        Link Exisiting
                      </AssignLoanFilesButton>
                    </Flx>
                  }
                  bodySx={{ p: 0 }}
                >
                  <LoanBorrowerAssociatedFilesTable
                    borrower={borrower}
                    loan={loan}
                  />
                </PanelCard>
              </ContentArea>
            </TabContent>

            {/* ── Applicant Info TAB ── */}
            <TabContent tabKey={TABS.APPLICANT_INFO} activeTab={primaryTab}>
              <PanelCardCollapseProvider value={cardSignal}>
                <Box>
                <RffFormChanges
                  initialValues={borrower}
                  onSubmit={handleInlineSave}
                  loading={saving}
                  // Required by the experience count buckets. update_borrower
                  // $sets each top-level key whole, so a partial nested payload
                  // like { ff_sold: { less_than_three_years: 4 } } would wipe
                  // greater_than_three_years. sendWholeObject swaps any changed
                  // nested object for the complete one from form values. Same
                  // flag BorrowerEditForm already uses, and a no-op for the
                  // scalar fields on this tab.
                  sendWholeObject
                  suppressFooter
                  // Floating save pill: rendered at the bottom and kept mounted so it can
                  // slide out / hold its "Saved" state after the last change clears.
                  hasChangesContentPosition="bottom"
                  persistChangesContent
                  hasChangesContent={
                    <RffPendingChangesBanner
                      initialValues={borrower}
                      loading={saving}
                    />
                  }
                >
                  <GuidelineAlerts checks={borrower?.borrowerGuidelineChecks} />

                  {/* Sub-tab switcher over search + expand/collapse, both
                      pinned. One sticky wrapper rather than two stacked sticky
                      rows — the switcher's height changes when it wraps, so a
                      hard `top` offset on the second row would drift. */}
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                      background: "#ffffff",
                      borderBottom: `1px solid ${grey[200]}`,
                    }}
                  >
                    <SubTabSwitcher
                      tabs={Object.values(BORROWER_TABS)}
                      value={borrowerTab}
                      onChange={setBorrowerTab}
                    />
                    <Flx ac g={1} sx={{ px: 1, py: 1 }}>
                      <Box sx={{ flex: "0 1 320px", minWidth: 160 }}>
                        <SearchInput
                          variant="paper"
                          rounded
                          placeholder="Search borrower fields..."
                          autoFocus
                          fullWidth
                          onClear={() => setQuickFilter("")}
                          value={quickFilter}
                          onChange={setQuickFilter}
                          sx={{
                            input: {
                              fontSize: "11px",
                              height: "14px",
                              px: 0.5,
                              py: 1,
                            },
                            svg: { fontSize: "14px !important" },
                          }}
                        />
                      </Box>
                      <CardCollapseButtons onSignal={signalCards} />
                    </Flx>
                  </Box>
                  <ContentArea>
                    <Box ref={attachMasonryRef} sx={masonrySx}>
                      {/* ── Sections conditionally rendered by sub-tab ── */}
                      {showBorrowerSection(BORROWER_TABS.DETAILS) && (
                        <ContactIdentitySection
                          borrower={borrower}
                          quickFilter={quickFilter}
                          onOpenAddress={() =>
                            setBorrowerTab(BORROWER_TABS.ADDRESS_DETAILS)
                          }
                        />
                      )}

                      {showBorrowerSection(BORROWER_TABS.LIQUIDITY) && (
                        <LiquiditySection
                          borrower={borrower}
                          quickFilter={quickFilter}
                        />
                      )}

                      {showBorrowerSection(BORROWER_TABS.LOAN_ROLE) && (
                        <LoanRoleSection
                          borrower={borrower}
                          quickFilter={quickFilter}
                        />
                      )}

                      {showBorrowerSection(BORROWER_TABS.CREDIT) && (
                        <CreditBackgroundSection
                          borrower={borrower}
                          quickFilter={quickFilter}
                        />
                      )}

                      {showBorrowerSection(BORROWER_TABS.BACKGROUND) && (
                        <HousingEventsSection
                          borrower={borrower}
                          quickFilter={quickFilter}
                        />
                      )}

                      {showBorrowerSection(BORROWER_TABS.EXPERIENCE) && (
                        <ExperienceSection
                          borrower={borrower}
                          quickFilter={quickFilter}
                        />
                      )}

                      {showBorrowerSection(BORROWER_TABS.NOTES) && (
                        <NotesSection
                          borrower={borrower}
                          quickFilter={quickFilter}
                        />
                      )}

                      {/* Address Details — the borrower's primary address.
                          Self-contained editor (or read-only non-US display);
                          not part of the RffFormChanges field set. Rendered
                          last so it sits at the bottom in the "All" view. */}
                      {/* Address is its own sub-tab only — deliberately absent
                          from "All". The editor is tall (autocomplete + manual
                          entry + geocode badge) and unbalanced the column
                          stack; the Contact & Identity address row links here
                          for anyone who arrives looking for it. */}
                      {borrowerTab === BORROWER_TABS.ADDRESS_DETAILS &&
                        (borrower?.nonUSborrowerAddress ? (
                          // Non-US addresses skip the US geocoder flow — keep
                          // the read-only display + per-section inline edit.
                          <PanelCard
                            headerEndContent={
                              <QuickActionButton
                                onClick={() =>
                                  handleStartEdit(EDIT_SECTIONS.ADDRESS)
                                }
                                startIcon={
                                  <EditLocationAltOutlined className="thin" />
                                }
                              >
                                Edit Address
                              </QuickActionButton>
                            }
                            title="Non-US Address"
                            py={2}
                            icon={<HouseOutlined />}
                            variant="loanInfo"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                bgcolor: grey[50],
                                p: 1.5,
                                flexWrap: "wrap",
                                justifyContent: "space-between",
                                borderRadius: "8px",
                                border: `1px solid ${grey[200]}`,
                              }}
                            >
                              <Flx jb fw ac sx={{ gap: 0.5 }}>
                                <Flx sx={{ alignItems: "center", gap: 0.5 }}>
                                  <LocationOnOutlined
                                    sx={{
                                      fontSize: 18,
                                      color: grey[400],
                                      mt: "2px",
                                    }}
                                  />
                                  <Txt
                                    sx={{
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: grey[800],
                                    }}
                                  >
                                    {borrower?.nonUSborrowerAddress}
                                  </Txt>
                                </Flx>
                                <OpenLocationButton
                                  address={borrower?.nonUSborrowerAddress}
                                />
                              </Flx>
                            </Box>
                          </PanelCard>
                        ) : (
                          // US primary address — self-contained view/modify
                          // editor mirroring the property sidebar's
                          // PropertyAddressEditor (Places autocomplete + manual
                          // entry + geocode badge), wired to the borrower
                          // address endpoints.
                          <PanelCard
                            title="Primary Address"
                            py={2}
                            icon={<HouseOutlined />}
                            variant="loanInfo"
                          >
                            <BorrowerAddressEditor
                              borrower={borrower}
                              loanId={loanId}
                            />
                          </PanelCard>
                        ))}
                    </Box>
                  </ContentArea>
                  </RffFormChanges>
                </Box>
              </PanelCardCollapseProvider>
            </TabContent>

            {/* ── KEY DOCUMENTS TAB ── */}
            <TabContent tabKey={TABS.KEY_DOCUMENTS} activeTab={primaryTab}>
              <ContentArea>
                {/* Credit auth / loan application, credit report, background
                    check and photo ID — each a box that attaches or uploads
                    its document and sets its date, with validity judged by
                    the shared document rules. Replaced the older
                    required-documents checklist; the same boxes used to sit on
                    the Applicants-tab card. */}
                <PanelCard
                  title="Key Documents & Reports"
                  py={2}
                  icon={<EventNoteOutlined />}
                  subTitle="Each item's document and date. Click one to attach, upload or update it."
                >
                  <Box sx={{ mt: 1 }}>
                    <ApplicantKeyDatesSection borrower={borrower} />
                  </Box>
                </PanelCard>
              </ContentArea>
            </TabContent>

            {/* ── LINKED DOCUMENTS TAB ── */}
            <TabContent tabKey={TABS.FILES} activeTab={primaryTab}>
              {FILES_TAB_LAYOUT === "assigned" ? (
                // The Applicants-tab card's Assigned Documents rail, in a
                // white card with the group cards wrapping into columns.
                <ContentArea>
                  <Box
                    sx={{
                      border: `1px solid ${slate[200]}`,
                      borderRadius: "10px",
                      background: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <RecordDocCoveragePanel
                      assignTo={{
                        type: "borrower",
                        id: borrower?._id,
                        label: sidebarTitle || borrower?.emailAddress || "Borrower",
                      }}
                      files={borrowerFiles}
                      linkedDocumentIds={borrower?.associations?.loanDocuments || []}
                      masonry
                    />
                  </Box>
                </ContentArea>
              ) : (
              <ContentArea>
                <Flx column g={0}>
                  <Box>
                    <PanelCard
                      disableCollapse
                      title="Upload"
                      subTitle="Directly upload files associated with this borrower."
                      bodySx={{ py: 1.5 }}
                      icon={<CloudUploadOutlined />}
                    >
                      <LoanBorrowerFileUploadProcess
                        borrowerId={borrower?._id}
                        showDivider={false}
                        loanId={loan?._id}
                        perFileDocType
                      />
                    </PanelCard>
                  </Box>
                  <PanelCard
                    icon={<ImageOutlined />}
                    title={`Related Borrower Files (${size(borrowerFiles)})`}
                    subTitle="All files associated & linked to this borrower."
                    headerEndContent={
                      <AssignLoanFilesButton
                        sx={{ mr: 0.5 }}
                        borrower={borrower}
                      />
                    }
                    bodySx={{ p: 0 }}
                  >
                    <LoanBorrowerAssociatedFilesTable
                      borrower={borrower}
                      loan={loan}
                    />
                  </PanelCard>
                </Flx>
              </ContentArea>
              )}
            </TabContent>

            {/* ── COMMENTS TAB ── */}
            <TabContent tabKey={TABS.COMMENTS} activeTab={primaryTab}>
              <ContentArea>
                <FieldsSection
                  // disableCollapse
                  title="Comments"
                  icon={<CommentOutlined />}
                  subTitle="Submit and view comments associated with this borrower."
                  // hideBorder
                >
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <LoanEnhancedCommentTextBox
                      key={commentKey}
                      loan={loan}
                      borrowerIds={borrower?._id ? [borrower._id] : []}
                      onSuccessfn={() => setCommentKey((k) => k + 1)}
                    />
                  </Box>
                  <BorrowerCommentsRenderer borrowerId={borrower?._id} />
                </FieldsSection>
              </ContentArea>
            </TabContent>

            {/* ── JSON TAB ── */}
            <TabContent tabKey={TABS.JSON} activeTab={primaryTab}>
              <ContentArea>
                <JsonPreview values={borrower} defaultCollapse={3} />
              </ContentArea>
            </TabContent>
          </SidebarBodyContent>
        </>
      )}

      <ConfirmationDialog
        open={confirmDelete}
        title="Delete Borrower?"
        message={`Are you sure you want to remove ${
          [borrower?.firstName, borrower?.lastName].filter(Boolean).join(" ") ||
          "this borrower"
        } from the loan? This will also remove their association with any files. You can restore them from the loan's deletedBorrowers list.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </ResizeableSidebar>
  );
};

export default LoanBorrowerSidebar;
